import React from 'react';
import { BookOpen, FileText, Trophy, List } from 'lucide-react';
import { getRyanMedicalIcon } from '../constants/ryanMedicalIcons';
import { getRyanFieldTones } from '../constants/ryanFieldVisuals';
import './RyanFieldProgressHero.css';

export interface RyanProgressSubdeck {
  _id: string;
  name: string;
  iconId?: string | null;
  /** Domínio (flashcards) ou % de páginas lidas (apostila), a depender de `mode`. */
  masteryPercentage: number | null;
  lastReviewedAt?: string | null;
  deckId: string;
}

interface RyanFieldProgressHeroProps {
  colorId: string;
  /** Quando a aba não tem nenhum deck de flashcards (só apostilas), o hero
      inteiro troca pra progresso de leitura em vez de domínio. */
  mode: 'flashcards' | 'apostila';
  lastStudied: RyanProgressSubdeck | null;
  topDomains: RyanProgressSubdeck[];
  onContinue: (subdeck: RyanProgressSubdeck) => void;
  onOpenRanking: () => void;
}

function segmentsFor(pct: number) {
  return [0, 1, 2, 3, 4].map(i => Math.min(100, Math.max(0, (pct - i * 20) / 20 * 100)));
}

const RyanFieldProgressHero: React.FC<RyanFieldProgressHeroProps> = ({
  colorId,
  mode,
  lastStudied,
  topDomains,
  onContinue,
  onOpenRanking,
}) => {
  const tones = getRyanFieldTones(colorId);
  const isApostilaMode = mode === 'apostila';
  const DefaultIcon = isApostilaMode ? FileText : BookOpen;
  const ContinueIcon = (lastStudied && getRyanMedicalIcon(lastStudied.iconId)) || DefaultIcon;
  const continuePct = lastStudied?.masteryPercentage ?? 0;
  const metricLabel = isApostilaMode ? 'Leitura' : 'Domínio';

  return (
    <div className="rfp-hero">
      <div
        className="rfp-continue"
        style={{ background: tones.base }}
        onClick={lastStudied ? () => onContinue(lastStudied) : undefined}
        role={lastStudied ? 'button' : undefined}
        tabIndex={lastStudied ? 0 : undefined}
      >
        {lastStudied ? (
          <>
            <div className="rfp-continue-top">
              <div className="rfp-continue-icon">
                <ContinueIcon size={20} />
              </div>
              <div className="rfp-continue-text">
                <p className="rfp-continue-label">Continuar de onde parou</p>
                <p className="rfp-continue-name">{lastStudied.name}</p>
              </div>
            </div>
            <div className="rfp-continue-bottom">
              <div className="rfp-mastery-row">
                <span>{metricLabel}</span>
                <b>{continuePct}%</b>
              </div>
              <div className="rfp-segs">
                {segmentsFor(continuePct).map((fill, i) => (
                  <div key={i} className="rfp-seg">
                    <div className="rfp-seg-fill" style={{ width: `${fill}%` }} />
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="rfp-continue-empty">
            <DefaultIcon size={22} />
            <p>{isApostilaMode ? 'Você ainda não leu nenhuma apostila nesta aba.' : 'Você ainda não estudou nenhum deck nesta aba.'}</p>
          </div>
        )}
      </div>

      <div className="rfp-top3" style={{ background: tones.secondary }}>
        <div className="rfp-top3-head">
          <p className="rfp-top3-title">
            <Trophy size={13} /> {isApostilaMode ? 'Top apostilas' : 'Top domínio'}
          </p>
          <button type="button" className="rfp-ver-todos" onClick={onOpenRanking}>
            <List size={12} /> Ver todos
          </button>
        </div>

        {topDomains.length > 0 ? (
          <div className="rfp-rank-list">
            {topDomains.map((s, i) => {
              const pct = s.masteryPercentage ?? 0;
              return (
                <div key={s._id} className="rfp-rank-item">
                  <span className="rfp-rank-pos">#{i + 1}</span>
                  <span className="rfp-rank-name">{s.name}</span>
                  <div className="rfp-rank-bar">
                    <div className="rfp-rank-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="rfp-rank-pct">{pct}%</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="rfp-top3-empty">{isApostilaMode ? 'Nenhuma apostila lida ainda nesta aba.' : 'Nenhum deck estudado ainda nesta aba.'}</p>
        )}
      </div>
    </div>
  );
};

export default RyanFieldProgressHero;
